// v3.15-1089 — Admin Template Management UI (/admin/email-templates).
//
// Single page that lists every template in `email_templates_v2`, supports
// search by name/key/category/status, and exposes Create / Edit / Clone /
// Disable / Enable / Preview / Audit-history actions. The Create+Edit modal
// (TemplateEditor) ships with a visual variable palette: clicking a chip
// inserts the variable at the cursor in either the Subject or Body field.
//
// All template mutations go through `/api/email/templates/v2/*`. Audit log
// per row is exposed via `/api/email/templates/v2/{id}/audit`.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Plus, Search, Edit2, Copy, Power, History, Eye, Loader2,
  FileText, AlertCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  client_communication: 'Client Communication',
  funding:              'Funding',
  risk:                 'Risk',
  internal:             'Internal',
  custom:               'Custom',
};
const CATEGORY_VALUES = ['client_communication', 'funding', 'risk', 'internal', 'custom'];

// ---------------- Variable palette (clickable chips) ----------------
const VariablePalette = ({ variables, onInsert, busy }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="variable-palette">
    {variables.map((v) => (
      <button
        key={v.name}
        type="button"
        disabled={busy}
        onClick={() => onInsert(v.name)}
        title={v.description}
        className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono rounded border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:border-amber-300 transition-colors"
        data-testid={`variable-chip-${v.name}`}
      >
        {`{${v.name}}`}
      </button>
    ))}
  </div>
);

// ---------------- Template Editor (Create / Edit) ----------------
const TemplateEditor = ({ open, onOpenChange, template, onSaved }) => {
  const isEditing = !!template?.id;
  const [name, setName]         = useState('');
  const [category, setCategory] = useState('custom');
  const [subject, setSubject]   = useState('');
  const [body, setBody]         = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving]     = useState(false);
  const [variables, setVariables] = useState([]);
  // Live preview
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody]       = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  // Focused field tracking so chips know where to insert
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const [focusedField, setFocusedField] = useState('body');

  useEffect(() => {
    if (!open) return;
    setName(template?.name || '');
    setCategory(template?.category || 'custom');
    setSubject(template?.subject || '');
    setBody(template?.body_html || template?.body_text || template?.body || '');
    setDescription(template?.description || '');
    setPreviewSubject('');
    setPreviewBody('');
    // Lazy-load the variable allow-list once per open.
    api.get('/email/templates/variables')
      .then(({ data }) => setVariables(data?.variables || []))
      .catch(() => setVariables([]));
  }, [open, template]);

  // Debounced live preview (against an empty context — admin preview)
  useEffect(() => {
    if (!open) return;
    let alive = true;
    const t = setTimeout(async () => {
      if (!subject && !body) return;
      setPreviewLoading(true);
      try {
        // Preview the in-flight draft by temporarily POSTing the variables.
        // Since the preview endpoint takes a saved template_id, we fall back
        // to a stub: replace `{var}` tokens against a fixture context.
        const fixture = {
          client_name: 'Jane Smith',
          client_first_name: 'Jane',
          client_email: 'jane@example.com',
          client_phone: '(555) 123-4567',
          company_name: 'Prestigious Companies',
          deal_amount: '$25,000',
          funding_amount: '$25,000',
          funding_status: 'Approved',
          current_stage: 'Funding Confirmation',
          sales_rep_name: 'Adris Davis',
          manager_name: 'Adris Davis',
          director_name: 'Adris Davis',
          concierge_name: 'Adris Davis',
          application_manager_name: 'Adris Davis',
          vo_officer_name: 'Adris Davis',
          sender_name: 'Adris Davis',
          sender_email: 'a.davis@prestigioustravelclub.org',
          platform_name: 'Prestigious Companies',
          date: 'Monday, June 9',
          time: '11:30 AM EST',
        };
        const subst = (s) => (s || '').replace(/\{([a-z][a-z0-9_]{1,40})\}/g, (m, key) => (
          Object.prototype.hasOwnProperty.call(fixture, key) ? fixture[key] : m
        ));
        if (!alive) return;
        setPreviewSubject(subst(subject));
        setPreviewBody(subst(body));
      } finally {
        if (alive) setPreviewLoading(false);
      }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [subject, body, open]);

  const insertVar = useCallback((varName) => {
    const token = `{${varName}}`;
    if (focusedField === 'subject') {
      const el = subjectRef.current;
      const start = el?.selectionStart ?? subject.length;
      const end   = el?.selectionEnd   ?? subject.length;
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      setTimeout(() => {
        el?.focus();
        const pos = start + token.length;
        el?.setSelectionRange(pos, pos);
      }, 0);
    } else {
      const el = bodyRef.current;
      const start = el?.selectionStart ?? body.length;
      const end   = el?.selectionEnd   ?? body.length;
      const next = body.slice(0, start) + token + body.slice(end);
      setBody(next);
      setTimeout(() => {
        el?.focus();
        const pos = start + token.length;
        el?.setSelectionRange(pos, pos);
      }, 0);
    }
  }, [focusedField, subject, body]);

  const handleSave = async () => {
    if (!name.trim())    { toast.error('Name is required'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Body is required'); return; }
    setSaving(true);
    try {
      const payload = { category, name, subject, body, description };
      if (isEditing) {
        await api.put(`/email/templates/v2/${template.id}`, payload);
        toast.success(`Template "${name}" updated`);
      } else {
        await api.post('/email/templates/v2', payload);
        toast.success(`Template "${name}" created`);
      }
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {isEditing ? `Edit Template — ${template?.name}` : 'Create New Template'}
            {isEditing && template?.is_seed && (
              <Badge variant="outline" className="ml-2 text-[10px]">Seed</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Editor column */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-1">
                <Label className="text-xs text-slate-600">Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="editor-name"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-xs text-slate-600">Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-9 text-sm" data-testid="editor-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_VALUES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`editor-category-${c}`}>
                        {CATEGORY_LABELS[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-9 text-sm"
                placeholder="When to use this template…"
                data-testid="editor-description"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Available variables — click to insert</Label>
              <div className="mt-1 p-2 border border-slate-200 rounded bg-slate-50">
                {variables.length === 0 ? (
                  <div className="text-[11px] text-slate-400 italic">Loading variables…</div>
                ) : (
                  <VariablePalette variables={variables} onInsert={insertVar} busy={saving} />
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                Chips insert into the field you most recently clicked into (Subject or Body).
              </p>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Subject *</Label>
              <Input
                ref={subjectRef}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onFocus={() => setFocusedField('subject')}
                className="h-9 text-sm font-mono"
                data-testid="editor-subject"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Body *</Label>
              <textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onFocus={() => setFocusedField('body')}
                className="w-full text-xs font-mono border border-slate-200 rounded p-2 min-h-[260px]"
                data-testid="editor-body"
              />
            </div>
          </div>

          {/* Live preview column */}
          <div className="space-y-2" data-testid="editor-preview">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-600 uppercase tracking-wide font-semibold">Live preview</Label>
              {previewLoading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
            </div>
            <div className="border border-slate-200 rounded bg-white">
              <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50 text-[11px]">
                <span className="text-slate-400">Subject:</span>{' '}
                <span className="font-medium text-slate-800" data-testid="editor-preview-subject">{previewSubject || '(empty)'}</span>
              </div>
              <pre className="px-3 py-2 text-[12px] leading-relaxed whitespace-pre-wrap font-sans text-slate-800 max-h-[420px] overflow-y-auto" data-testid="editor-preview-body">
                {previewBody || '(empty)'}
              </pre>
            </div>
            <p className="text-[10px] text-slate-400">
              Preview substitutes a fixture client context — actual sends resolve from the deal.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving} data-testid="editor-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="editor-save">
            {saving ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving…</> : (isEditing ? 'Save Changes' : 'Create Template')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Audit-history Modal ----------------
const AuditHistoryModal = ({ open, onOpenChange, template }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !template?.id) return;
    setLoading(true);
    api.get(`/email/templates/v2/${template.id}/audit`)
      .then(({ data }) => {
        setEntries(data?.entries || []);
        setMeta(data?.template || null);
      })
      .catch(() => toast.error('Failed to load audit history'))
      .finally(() => setLoading(false));
  }, [open, template]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-history-modal">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" /> Audit History — {template?.name}
          </DialogTitle>
        </DialogHeader>
        {meta && (
          <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border border-slate-200 rounded p-3 mb-2" data-testid="audit-meta-block">
            <div><span className="text-slate-400">Key:</span> <span className="font-mono">{meta.key}</span></div>
            <div><span className="text-slate-400">Category:</span> {CATEGORY_LABELS[meta.category] || meta.category}</div>
            <div><span className="text-slate-400">Status:</span> {meta.is_active ? <Badge>Active</Badge> : <Badge variant="destructive">Disabled</Badge>}</div>
            <div><span className="text-slate-400">Seed:</span> {meta.is_seed ? 'Yes' : 'No'}</div>
            <div className="col-span-2"><span className="text-slate-400">Created:</span> {meta.created_at?.slice(0,19)} by {meta.created_by_name || '—'}</div>
            <div className="col-span-2"><span className="text-slate-400">Last Modified:</span> {meta.updated_at?.slice(0,19)} by {meta.updated_by_name || '—'}</div>
            {meta.disabled_at && (
              <div className="col-span-2 text-red-700">
                <span className="text-slate-400">Disabled:</span> {meta.disabled_at.slice(0,19)} by {meta.disabled_by_name || '—'}
              </div>
            )}
            {meta.clone_source && (
              <div className="col-span-2">
                <span className="text-slate-400">Cloned from:</span> <span className="font-mono">{meta.clone_source.key}</span> ({meta.clone_source.name})
              </div>
            )}
          </div>
        )}
        {loading ? (
          <div className="text-center py-6 text-xs text-slate-400 flex items-center justify-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">No audit entries.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="audit-entries">
            {entries.map((e) => (
              <AuditEntryRow key={e.id} entry={e} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AuditEntryRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const hasDiff = entry.before || entry.after;
  const colour = {
    created:  'bg-green-50 border-green-200 text-green-800',
    edited:   'bg-blue-50 border-blue-200 text-blue-800',
    cloned:   'bg-purple-50 border-purple-200 text-purple-800',
    disabled: 'bg-red-50 border-red-200 text-red-800',
    enabled:  'bg-emerald-50 border-emerald-200 text-emerald-800',
  }[entry.action] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <li className={`border rounded ${colour}`} data-testid={`audit-entry-${entry.action}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-2.5 py-1.5 flex items-center gap-2 text-[12px]"
      >
        {hasDiff ? (open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : <span className="w-3" />}
        <span className="font-medium uppercase tracking-wide text-[10px]">{entry.action}</span>
        <span className="flex-1">by {entry.by_user_name || entry.by_user_email}</span>
        <span className="text-[10px] opacity-70">{entry.at?.slice(0,19)?.replace('T', ' ')}</span>
      </button>
      {open && hasDiff && (
        <pre className="px-3 pb-2 text-[10px] whitespace-pre-wrap font-mono">
{JSON.stringify({ before: entry.before, after: entry.after, clone_source: entry.clone_source }, null, 2)}
        </pre>
      )}
    </li>
  );
};

// ---------------- Preview Modal ----------------
const PreviewModal = ({ open, onOpenChange, template }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);

  useEffect(() => {
    if (!open || !template?.id) return;
    setLoading(true);
    api.post('/email/templates/preview', { template_id: template.id })
      .then(({ data }) => setResult(data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Preview failed'))
      .finally(() => setLoading(false));
  }, [open, template]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="preview-modal">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4" /> Preview — {template?.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-6 text-xs text-slate-400 flex items-center justify-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Rendering…
          </div>
        ) : !result ? (
          <div className="text-center py-6 text-xs text-slate-400">No preview available.</div>
        ) : (
          <div className="space-y-2">
            <div className="border border-slate-200 rounded">
              <div className="px-3 py-1.5 border-b bg-slate-50 text-[11px]">
                <span className="text-slate-400">Subject:</span>{' '}
                <span className="font-medium" data-testid="preview-subject">{result.rendered_subject || '(empty)'}</span>
              </div>
              <pre className="px-3 py-2 text-[12px] whitespace-pre-wrap font-sans max-h-[420px] overflow-y-auto" data-testid="preview-body">
                {result.rendered_body || '(empty)'}
              </pre>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="bg-slate-50 border rounded p-2" data-testid="preview-vars-resolved">
                <div className="font-semibold text-slate-600 mb-1">Variables resolved ({result.variables_resolved?.length || 0})</div>
                {(result.variables_resolved || []).length === 0
                  ? <div className="text-slate-400 italic">None</div>
                  : <div className="font-mono text-slate-700">{(result.variables_resolved || []).join(', ')}</div>}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2" data-testid="preview-vars-unknown">
                <div className="font-semibold text-amber-700 mb-1">Unknown / unresolved ({result.variables_unknown?.length || 0})</div>
                {(result.variables_unknown || []).length === 0
                  ? <div className="text-slate-400 italic">None</div>
                  : <div className="font-mono text-amber-900">{(result.variables_unknown || []).join(', ')}</div>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Main Page ----------------
const AdminEmailTemplatesPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading]     = useState(true);
  const [templates, setTemplates] = useState([]);
  const [filterQ, setFilterQ]     = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionTpl, setActionTpl] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterQ.trim())              params.q = filterQ.trim();
      if (filterCat !== 'all')         params.category = filterCat;
      if (filterStatus !== 'all')      params.status = filterStatus;
      const { data } = await api.get('/email/templates/v2/admin', { params });
      setTemplates(data?.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [filterQ, filterCat, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Category counts shown in the header (always reflects the *current
  // filtered* result set so admins can confirm what they're looking at).
  const categoryCounts = useMemo(() => {
    const counts = { client_communication: 0, funding: 0, risk: 0, internal: 0, custom: 0 };
    for (const t of templates) {
      if (counts[t.category] !== undefined) counts[t.category]++;
    }
    return counts;
  }, [templates]);

  const totalActive   = useMemo(() => templates.filter((t) => t.is_active).length, [templates]);
  const totalDisabled = useMemo(() => templates.filter((t) => !t.is_active).length, [templates]);

  // ---- Actions ----
  const openCreate = () => { setEditorTemplate(null); setEditorOpen(true); };
  const openEdit   = (tpl) => { setEditorTemplate(tpl); setEditorOpen(true); };
  const openAudit  = (tpl) => { setActionTpl(tpl); setAuditOpen(true); };
  const openPreview = (tpl) => { setActionTpl(tpl); setPreviewOpen(true); };
  const clone = async (tpl) => {
    setBusyId(tpl.id);
    try {
      await api.post(`/email/templates/v2/${tpl.id}/clone`, {});
      toast.success(`Cloned "${tpl.name}"`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clone failed');
    } finally {
      setBusyId(null);
    }
  };
  const disable = async (tpl) => {
    setBusyId(tpl.id);
    try {
      await api.post(`/email/templates/v2/${tpl.id}/disable`);
      toast.success(`"${tpl.name}" disabled`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disable failed');
    } finally {
      setBusyId(null);
    }
  };
  const enable = async (tpl) => {
    setBusyId(tpl.id);
    try {
      await api.post(`/email/templates/v2/${tpl.id}/enable`);
      toast.success(`"${tpl.name}" enabled`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enable failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin?.()) {
    return (
      <div className="p-8 text-center text-slate-500 text-sm">
        <AlertCircle className="h-5 w-5 mx-auto mb-2 text-amber-500" />
        Admin access required.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-email-templates-page">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            Email Templates
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Universal registry — every template used by the platform lives here (v3.15-1089).
          </p>
        </div>
        <Button onClick={openCreate} className="gap-1.5" data-testid="admin-create-btn">
          <Plus className="h-4 w-4" /> New Template
        </Button>
      </div>

      {/* Top metrics + filters */}
      <div className="bg-white border border-slate-200 rounded p-3 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <Badge variant="outline" data-testid="admin-stat-total">Total: {templates.length}</Badge>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="admin-stat-active">Active: {totalActive}</Badge>
          <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200" data-testid="admin-stat-disabled">Disabled: {totalDisabled}</Badge>
          <span className="w-px h-4 bg-slate-200" />
          {CATEGORY_VALUES.map((c) => (
            <Badge key={c} variant="outline" className="bg-amber-50 border-amber-200 text-amber-900" data-testid={`admin-stat-cat-${c}`}>
              {CATEGORY_LABELS[c]}: {categoryCounts[c]}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={filterQ}
              onChange={(e) => setFilterQ(e.target.value)}
              placeholder="Search by name / key / subject / description…"
              className="pl-7 h-9 text-sm"
              data-testid="admin-search"
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="h-9 text-sm w-[200px]" data-testid="admin-filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="admin-filter-category-all">All categories</SelectItem>
              {CATEGORY_VALUES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`admin-filter-category-${c}`}>{CATEGORY_LABELS[c]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 text-sm w-[160px]" data-testid="admin-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all"      data-testid="admin-filter-status-all">All statuses</SelectItem>
              <SelectItem value="active"   data-testid="admin-filter-status-active">Active</SelectItem>
              <SelectItem value="disabled" data-testid="admin-filter-status-disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded overflow-hidden" data-testid="admin-templates-table">
        {loading ? (
          <div className="py-12 text-center text-xs text-slate-400 flex items-center justify-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="py-12 text-center text-xs text-slate-400">No templates match.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Key</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Category</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Status</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Updated</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr
                  key={t.id}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!t.is_active ? 'opacity-60' : ''}`}
                  data-testid={`admin-row-${t.key}`}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{t.name}</div>
                    {t.description && (
                      <div className="text-[10px] text-slate-400 line-clamp-1">{t.description}</div>
                    )}
                    {t.is_seed && <Badge variant="outline" className="text-[9px] mt-0.5">Seed</Badge>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-500 hidden md:table-cell">{t.key}</td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <Badge variant="outline" className="text-[10px]">{CATEGORY_LABELS[t.category] || t.category}</Badge>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    {t.is_active
                      ? <Badge className="text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                      : <Badge variant="destructive" className="text-[10px]">Disabled</Badge>}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500 hidden lg:table-cell">
                    {t.updated_at ? t.updated_at.slice(0,10) : '—'}
                    {t.updated_by_name && <div className="text-[10px] text-slate-400">by {t.updated_by_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-0.5">
                      <Button size="sm" variant="ghost" onClick={() => openPreview(t)} disabled={busyId === t.id} title="Preview" data-testid={`admin-action-preview-${t.key}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)} disabled={busyId === t.id} title="Edit" data-testid={`admin-action-edit-${t.key}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => clone(t)} disabled={busyId === t.id} title="Clone" data-testid={`admin-action-clone-${t.key}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openAudit(t)} disabled={busyId === t.id} title="Audit history" data-testid={`admin-action-audit-${t.key}`}>
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      {t.is_active ? (
                        <Button size="sm" variant="ghost" onClick={() => disable(t)} disabled={busyId === t.id} title="Disable" className="text-red-600 hover:text-red-700" data-testid={`admin-action-disable-${t.key}`}>
                          {busyId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => enable(t)} disabled={busyId === t.id} title="Enable" className="text-emerald-600 hover:text-emerald-700" data-testid={`admin-action-enable-${t.key}`}>
                          {busyId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editorTemplate}
        onSaved={fetchAll}
      />
      <AuditHistoryModal open={auditOpen} onOpenChange={setAuditOpen} template={actionTpl} />
      <PreviewModal open={previewOpen} onOpenChange={setPreviewOpen} template={actionTpl} />
    </div>
  );
};

export default AdminEmailTemplatesPage;
